"use client";

import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

function normalize(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export default function BusinessDirectoryTools() {
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState("Tous");
  const [query, setQuery] = useState("");
  const [resultCount, setResultCount] = useState(0);

  useEffect(() => {
    const root = document.querySelector<HTMLElement>("[data-business-directory]");
    if (!root) return;

    const groups = Array.from(
      root.querySelectorAll<HTMLElement>(".liens-bloc-groupe-container")
    );
    const names = groups
      .map((group) =>
        group.querySelector<HTMLElement>(".liens-bloc-groupe-titre")?.textContent?.trim()
      )
      .filter((name): name is string => Boolean(name));
    setCategories(["Tous", ...names]);

    root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
      if (link.dataset.enhanced) return;
      const number = link.textContent?.trim() || "";
      link.dataset.enhanced = "true";
      link.classList.add("business-call-button");
      link.textContent = `Appeler · ${number}`;
    });

    root.querySelectorAll<HTMLElement>(".un-lien-bloc").forEach((card) => {
      const footer = card.querySelector<HTMLElement>(".un-lien-bloc-footer");
      const address = card.querySelector<HTMLElement>(".un-lien-bloc-adresse")?.innerText.trim();
      if (!footer || !address || footer.querySelector(".business-route-button")) return;
      const link = document.createElement("a");
      link.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.className = "business-route-button";
      link.textContent = "Itinéraire";
      footer.appendChild(link);
    });

    root.querySelectorAll<HTMLAnchorElement>(".un-lien-bloc-email").forEach((link) => {
      if (!link.textContent?.trim()) link.textContent = "Envoyer un e-mail";
    });
    root.querySelectorAll<HTMLAnchorElement>(".un-lien-bloc-facebook").forEach((link) => {
      if (!link.textContent?.trim()) link.textContent = "Facebook";
    });
  }, []);

  useEffect(() => {
    const root = document.querySelector<HTMLElement>("[data-business-directory]");
    if (!root) return;
    let visible = 0;
    const wanted = normalize(query);

    root.querySelectorAll<HTMLElement>(".liens-bloc-groupe-container").forEach((group) => {
      const groupName =
        group.querySelector<HTMLElement>(".liens-bloc-groupe-titre")?.textContent?.trim() || "";
      const categoryMatches = category === "Tous" || groupName === category;
      let groupVisible = 0;

      group.querySelectorAll<HTMLElement>(".liens-bloc-groupe-ligne").forEach((card) => {
        const searchMatches = !wanted || normalize(card.innerText).includes(wanted);
        const matches = categoryMatches && searchMatches;
        card.hidden = !matches;
        if (matches) {
          visible += 1;
          groupVisible += 1;
        }
      });
      group.hidden = groupVisible === 0;
    });
    setResultCount(visible);
  }, [category, query, categories]);

  const resultLabel = useMemo(
    () => `${resultCount} ${resultCount > 1 ? "résultats" : "résultat"}`,
    [resultCount]
  );

  return (
    <>
    <style>{`
      .business-directory-tools { position:relative !important; top:auto !important; z-index:3; }
      .business-category-list { display:flex !important; flex-wrap:wrap !important; overflow:visible !important; padding-bottom:0 !important; }
      .business-category-select { display:none !important; }
      [data-business-directory] .liens-bloc-groupe-titre { position:relative !important; top:auto !important; z-index:1 !important; }
      .directory-public-page > header > div:last-child { padding-top:3rem !important; padding-bottom:3rem !important; }
      .directory-public-page > header h1 { margin-top:1rem !important; font-size:clamp(2.5rem,5vw,4.4rem) !important; }
      .directory-public-page > header h1 + div { margin-top:1.25rem !important; }
      .directory-public-page > section { padding-top:1.75rem !important; }
      @media (max-width:640px) {
        .business-category-list { display:none !important; }
        .business-category-select { display:grid !important; gap:.35rem; margin-top:.85rem; }
        .business-category-select select { width:100%; padding:.7rem .8rem; border:1px solid #cbd8e8; border-radius:.75rem; background:#fff; color:#253f6b; }
        .directory-public-page > header > div:last-child { padding-top:2.25rem !important; padding-bottom:2.25rem !important; }
        .directory-public-page > section { padding-top:1rem !important; }
      }
    `}</style>
    <div className="business-directory-tools" aria-label="Recherche dans l’annuaire">
      <div className="business-search-row">
        <label className="business-search-field">
          <Search aria-hidden="true" size={20} />
          <span className="sr-only">Rechercher une entreprise ou une activité</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher une entreprise, une activité…"
          />
        </label>
        <span className="business-result-count" aria-live="polite">{resultLabel}</span>
      </div>
      <div className="business-category-list" aria-label="Filtrer par catégorie">
        {categories.map((name) => (
          <button
            key={name}
            type="button"
            className={category === name ? "is-active" : ""}
            onClick={() => setCategory(name)}
          >
            {name}
          </button>
        ))}
      </div>
      <label className="business-category-select">
        <span>Catégorie</span>
        <select value={category} onChange={(event) => setCategory(event.target.value)}>
          {categories.map((name) => <option key={name}>{name}</option>)}
        </select>
      </label>
      {resultCount === 0 && (
        <p className="business-empty-state">Aucune entreprise ne correspond à votre recherche.</p>
      )}
    </div>
    </>
  );
}
